import type { FlowNodeData } from '$/domains/workFlowV3/type';
import type { SceneObject } from '@meitu/director-console';

export const DIRECTOR_CONSOLE_STATE_VERSION = 1;
export const DIRECTOR_CONSOLE_PANORAMA_API_NAME = 'image_weave_anything2img';
export const DIRECTOR_CONSOLE_PANORAMA_EFFECT_ID = '492413';
export const DIRECTOR_CONSOLE_DEFAULT_PANORAMA_RESOLUTION = '2K';
export const DIRECTOR_CONSOLE_PANORAMA_RATIO = '2:1';
export const DIRECTOR_CONSOLE_MODEL_MAX_SIZE = 200 * 1024 * 1024;

export type DirectorConsoleTool = 'move' | 'rotate' | 'scale';
export type DirectorConsolePanelLeftTab = 'scene' | 'assets';
export type DirectorConsolePanelRight = 'scene' | 'object' | 'camera' | 'cameraShots' | 'role' | 'rolePose';
export type DirectorConsoleCameraViewMode = 'compact' | 'expanded' | 'fullA' | 'fullB' | 'split';
export type DirectorConsoleObjectKind = 'geometry' | 'model' | 'role' | 'prop' | 'camera';
export type DirectorConsoleGeometryKind = 'box' | 'sphere' | 'cylinder' | 'plane' | 'torus' | 'cone' | 'pyramid';
export type DirectorConsoleModelLoadStatus = 'success' | 'failed' | 'uploading' | 'parsing' | 'loading';
export type DirectorConsolePanoramaResolution = '1K' | '2K' | '4K';
export type DirectorAiRecognitionTaskStatus = 'queued' | 'generating' | 'success' | 'failed';

export const DIRECTOR_CONSOLE_PANORAMA_EFFECT_IDS = {
  '1K': '493062',
  '2K': DIRECTOR_CONSOLE_PANORAMA_EFFECT_ID,
  '4K': '493063',
} as const satisfies Record<DirectorConsolePanoramaResolution, string>;

export type DirectorConsolePanoramaEffectId = (
  typeof DIRECTOR_CONSOLE_PANORAMA_EFFECT_IDS
)[DirectorConsolePanoramaResolution];

export type DirectorConsoleVector3 = [number, number, number];

export interface DirectorConsolePanoramaMeta {
  isPanorama720: boolean;
  apiName: typeof DIRECTOR_CONSOLE_PANORAMA_API_NAME;
  effectId: DirectorConsolePanoramaEffectId;
  ratio: typeof DIRECTOR_CONSOLE_PANORAMA_RATIO;
  resolution: DirectorConsolePanoramaResolution;
}

export interface DirectorConsoleScenePanorama {
  assetId?: number | string;
  url: string;
  width: number;
  height: number;
  source: 'canvas_connection' | 'upload' | 'manual';
  sphereRotation: number;
  sphereRadius: number;
}

export interface DirectorConsoleSceneState {
  zoom: number;
  pan: DirectorConsoleVector3;
  rotation: DirectorConsoleVector3;
  skyColor: string;
  frameAspectRatio: string;
  panorama?: DirectorConsoleScenePanorama;
  labelsVisible: boolean;
  gridSnapping: boolean;
  ground: {
    visible: boolean;
    opacity: number;
    height: number;
  };
}

export interface DirectorConsoleObjectState {
  id: string;
  name: string;
  kind: DirectorConsoleObjectKind;
  geometryKind?: DirectorConsoleGeometryKind;
  fileName?: string;
  format?: string;
  size?: number;
  url?: string;
  assetId?: number | string;
  loadStatus?: DirectorConsoleModelLoadStatus;
  errorMessage?: string;
  visible?: boolean;
  locked?: boolean;
  cameraFocalLength?: string;
  cameraTarget?: string;
  cameraTargetPosition?: DirectorConsoleVector3;
  rolePosePreset?: string;
  rolePoseControls?: Record<string, number>;
  position: DirectorConsoleVector3;
  rotation: DirectorConsoleVector3;
  scale: DirectorConsoleVector3;
  uniformScale: number;
  color: string;
}

export interface DirectorConsoleCameraShot {
  id: string;
  cameraId: string;
  title: string;
  imageNodeId?: string;
  assetId?: number | string;
  url: string;
  width: number;
  height: number;
  createdAt: number;
}

/** 导演台新版截图对应的持久资源信息，与通用 CameraShot 状态分开存储。 */
export interface DirectorConsoleShotResource {
  url: string;
  width?: number;
  height?: number;
  assetId?: number;
}

/** 机位首次创建/进入导演台时的完整镜头状态，用于“重置机位”恢复。 */
export type DirectorCameraHomeState = Pick<
SceneObject,
'position' | 'rotation' | 'scale' | 'fov' | 'frameAspectRatio'
| 'lookAt' | 'lookAtOrigin' | 'lookAtLocked' | 'lookAtTargetId' | 'rotationOrder'
>;

/** 绑定在导演台节点上的识图任务生命周期，用于刷新或重新打开后恢复查询、结果或失败提示。 */
export interface DirectorAiRecognitionPendingTask {
  taskIds: string[];
  imageUrl: string;
  imageCompressUrl?: string;
  sourceFingerprint?: string;
  createdAt: number;
  estimatedTotalMs?: number;
  /** 旧节点可能没有该字段，读取时按 queued 兼容。 */
  status?: DirectorAiRecognitionTaskStatus;
  appliedAt?: number;
  expiresAt?: number;
  failedAt?: number;
  errorMessage?: string;
}

export interface DirectorConsolePanelState {
  leftTab: DirectorConsolePanelLeftTab;
  rightPanel: DirectorConsolePanelRight;
  cameraViewMode: DirectorConsoleCameraViewMode;
}

export interface DirectorConsolePersistedState {
  scene: DirectorConsoleSceneState;
  objects: DirectorConsoleObjectState[];
  selectedIds: string[];
  activeTool: DirectorConsoleTool;
  activeCameraId: string;
  cameraShots: DirectorConsoleCameraShot[];
  panel: DirectorConsolePanelState;
}

export interface DirectorConsoleNodeData extends FlowNodeData {
  directorConsoleStateVersion?: number;
  directorConsoleState?: DirectorConsolePersistedState;
  /** @meitu/director-console 包的场景文档（DirectorSceneDocument），新版导演台的持久化载体。 */
  directorSceneDocument?: unknown;
  /** 截图 ID 到远程资源的宿主业务映射，用于后续发送画布时复用 assetId。 */
  directorCameraShotResources?: Record<string, DirectorConsoleShotResource>;
  /** 机位 ID 到创建状态的宿主业务映射，不写入通用场景协议。 */
  directorCameraHomes?: Record<string, DirectorCameraHomeState>;
  /** 天空颜色是否跟随界面主题；独立于通用场景协议，避免主题切换覆盖用户自定义色。 */
  directorSkyColorFollowsTheme?: boolean;
  /** 机位运镜时间轴；业务层独立协议，避免写入通用 3D 场景文档。 */
  directorMotionTimeline?: unknown;
  /** 当前导演台内部识图任务；不使用通用任务字段，避免生成结果节点或触发 BaseNode 任务态。 */
  directorAiRecognitionTask?: DirectorAiRecognitionPendingTask;
}

export interface PanoramaImageNodeData extends FlowNodeData {
  panoramaMeta?: DirectorConsolePanoramaMeta;
  panoramaPreviewMode?: boolean;
}
